import React from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO, isValid } from "date-fns";
import { enUS, es as esLocale } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

/**
 * DatePickerButton — a consistent date-picker across the Result page
 * (used by `DateTakenEditor` and `JournalCard`). Replaces the raw
 * `<input type="date">` with a shadcn Popover + Calendar so both
 * date-picking surfaces feel identical and match the rest of the app.
 *
 * Value contract: caller passes/receives an ISO `yyyy-mm-dd` string so
 * this stays a drop-in replacement for the previous date inputs. The
 * Calendar itself works in `Date`, so we parse-in / format-out.
 *
 * Locale: date-fns picks up the current i18n language to render month
 * names + day-of-week headers. Explicit list avoids pulling every
 * locale into the client bundle.
 */
export default function DatePickerButton({
  value,
  onChange,
  max,
  disabled,
  testId,
  ariaLabel,
  placeholder,
  size = "sm",
}) {
  const { i18n } = useTranslation();
  const locale = i18n.language === "es" ? esLocale : enUS;
  const parsed = value ? parseISO(value) : null;
  const selected = parsed && isValid(parsed) ? parsed : undefined;
  const maxParsed = max ? parseISO(max) : null;
  const maxDate = maxParsed && isValid(maxParsed) ? maxParsed : undefined;

  const label = selected
    ? format(selected, "PP", { locale })
    : (placeholder || "—");

  const [open, setOpen] = React.useState(false);
  const btnCls = size === "xs"
    ? "rounded-lg border border-[#E8E5DF] bg-white px-2 py-1 text-xs text-leaf-ink inline-flex items-center gap-1.5 hover:border-leaf/40 transition disabled:opacity-60"
    : "rounded-full bg-parchment/40 border border-[#E8E5DF] px-4 py-2 text-sm text-leaf-ink inline-flex items-center gap-2 w-full justify-between hover:border-leaf/40 transition disabled:opacity-60";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={testId}
          disabled={disabled}
          data-testid={testId}
          aria-label={ariaLabel}
          className={btnCls}
        >
          <span className="inline-flex items-center gap-2">
            <CalendarIcon className="w-3.5 h-3.5 text-leaf-mist" strokeWidth={1.5} aria-hidden="true" />
            <span className="[font-variant-numeric:lining-nums_tabular-nums]">{label}</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" data-testid={testId ? `${testId}-popover` : undefined}>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            if (!d) return;
            onChange(format(d, "yyyy-MM-dd"));
            setOpen(false);
          }}
          disabled={maxDate ? { after: maxDate } : undefined}
          initialFocus
          locale={locale}
        />
      </PopoverContent>
    </Popover>
  );
}
